// Boete (FO-033) — gedeelde DTOs + Zod.
//
// Een verkeersboete wordt geregistreerd met bedrag/datum/soort en gekoppeld aan
// óf direct een medewerker, óf een voertuig+datum waarna de bestuurder uit de
// VoertuigToewijzing-historie wordt afgeleid (resolver). Geld = Decimal/EUR;
// datum = NL-kalenderdag. Eén schema valideert client- en serverside.

import { z } from 'zod'
import { idParam } from './id-validators'

export const BOETE_SOORTEN = [
  'SNELHEID',
  'PARKEREN',
  'ROOD_LICHT',
  'MILIEUZONE',
  'TELEFOON_GEBRUIK',
  'OVERIG',
] as const
export type BoeteSoort = (typeof BOETE_SOORTEN)[number]

export const BOETE_SOORT_LABELS: Record<BoeteSoort, string> = {
  SNELHEID: 'Snelheid',
  PARKEREN: 'Parkeren',
  ROOD_LICHT: 'Rood licht',
  MILIEUZONE: 'Milieuzone',
  TELEFOON_GEBRUIK: 'Telefoongebruik',
  OVERIG: 'Overig',
}

export type BoeteBestuurderBron = 'DIRECT' | 'AUTO_RESOLVER' | 'HANDMATIG' | 'ONBEKEND'

export const BOETE_BESTUURDER_BRON_LABELS: Record<BoeteBestuurderBron, string> = {
  DIRECT: 'Direct gekoppeld',
  AUTO_RESOLVER: 'Afgeleid uit toewijzing',
  HANDMATIG: 'Handmatig toegewezen',
  ONBEKEND: 'Bestuurder onbekend',
}

const isoDate = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'Kies een geldige datum')

const boeteBase = {
  bedrag: z
    .number({ message: 'Vul een geldig bedrag in' })
    .positive('Bedrag moet groter dan 0 zijn')
    .max(1_000_000, 'Bedrag is onrealistisch hoog'),
  datum: isoDate,
  soort: z.enum(BOETE_SOORTEN),
  omschrijving: z.string().trim().max(2000).optional().nullable(),
  kenmerk: z.string().trim().max(100).optional().nullable(),
}

/** NL-kloktijd "uu:mm" van de overtreding. */
export const BOETE_TIJD_RE = /^([01]\d|2[0-3]):[0-5]\d$/

/** Verrekenperiode "jjjj-mm": het loon van die maand. */
export const BOETE_PERIODE_RE = /^\d{4}-(0[1-9]|1[0-2])$/

// batch-66 inkoop-afwijkingen (FO-497) — de velden van de boeteflow. Allemaal
// optioneel en nullable: de read-DTO geeft ze als null terug en het formulier
// mag ze zo terugsturen (read en write consistent).
const boeteFlowVelden = {
  administratieKosten: z
    .number({ message: 'Vul een geldig bedrag in' })
    .min(0, 'De administratiekosten kunnen niet negatief zijn')
    .max(100_000, 'De administratiekosten zijn onrealistisch hoog')
    .optional()
    .nullable(),
  overtredingTijd: z
    .string()
    .trim()
    .regex(BOETE_TIJD_RE, 'Vul de tijd in als uu:mm')
    .optional()
    .nullable(),
  locatie: z.string().trim().max(300, 'De locatie is te lang').optional().nullable(),
  feitcode: z.string().trim().max(50, 'De feitcode is te lang').optional().nullable(),
  overschrijdingKmu: z
    .number({ message: 'Vul een geheel getal in' })
    .int('Vul een geheel getal in')
    .min(0, 'De overschrijding kan niet negatief zijn')
    .max(300, 'De overschrijding is onrealistisch hoog')
    .optional()
    .nullable(),
}

/**
 * Aanmaken: óf `employeeId` (direct), óf `voertuigId` (resolver), exact één van
 * beide. Bij `voertuigId` leidt de API de bestuurder af uit `VoertuigToewijzing`.
 */
export const CreateBoeteSchema = z
  .object({
    ...boeteBase,
    ...boeteFlowVelden,
    employeeId: z.string().min(1).optional().nullable(),
    voertuigId: z.string().min(1).optional().nullable(),
  })
  .refine((d) => (d.employeeId ? 1 : 0) + (d.voertuigId ? 1 : 0) === 1, {
    message: 'Kies óf een medewerker, óf een voertuig (niet beide)',
    path: ['voertuigId'],
  })
export type CreateBoeteInput = z.infer<typeof CreateBoeteSchema>

/** Bijwerken van de inhoud (niet de bestuurder-koppeling). */
export const UpdateBoeteSchema = z.object({
  bedrag: boeteBase.bedrag.optional(),
  datum: isoDate.optional(),
  soort: z.enum(BOETE_SOORTEN).optional(),
  omschrijving: z.string().trim().max(2000).optional().nullable(),
  kenmerk: z.string().trim().max(100).optional().nullable(),
  verhaald: z.boolean().optional(),
  /**
   * Alleen samen met `verhaald: true`: het bedrag dat NU buiten de loonstrook om
   * is verhaald (hoogstens wat er openstaat; leeg = alles wat openstaat). Het
   * opgeslagen `verhaaldBedrag` is het totaal dat op de medewerker is verhaald en
   * rekent de server uit; los meegestuurd doet dit veld niets.
   */
  verhaaldBedrag: z.number().min(0).max(1_000_000).optional().nullable(),
  /**
   * batch-66 (FO-497) — de overtredingsdatum stond niet op de factuur en de
   * gebruiker bevestigt dat de ingevulde datum de datum van de overtreding is.
   * Daarna (en niet eerder) leidt het systeem een bestuurder af.
   */
  datumBevestigd: z.boolean().optional(),
  ...boeteFlowVelden,
})
export type UpdateBoeteInput = z.infer<typeof UpdateBoeteSchema>

/** Bestuurder handmatig (her)toewijzen, bv. na "onbekend". */
export const ReassignBoeteSchema = z.object({
  employeeId: z.string().min(1).nullable(),
})
export type ReassignBoeteInput = z.infer<typeof ReassignBoeteSchema>

export interface BoeteDTO {
  id: string
  bedrag: number
  datum: string
  soort: BoeteSoort
  soortLabel: string
  omschrijving: string | null
  kenmerk: string | null
  employeeId: string | null
  employeeNaam: string | null
  voertuigId: string | null
  kenteken: string | null
  bestuurderBron: BoeteBestuurderBron
  bestuurderBronLabel: string
  verhaald: boolean
  verhaaldBedrag: number | null
  verhaaldOp: string | null
  createdAt: string
  // batch-66 inkoop-afwijkingen (FO-497) — de boeteflow.
  status: BoeteStatus
  statusLabel: string
  bron: BoeteBron
  administratieKosten: number | null
  overtredingTijd: string | null
  locatie: string | null
  feitcode: string | null
  overschrijdingKmu: number | null
  /**
   * Wat er nog openstaat (euro): het bedrag min wat op de loonstrook is ingehouden
   * en wat buiten de loonstrook om is verhaald; 0 als de boete is afgesloten.
   */
  openBedrag: number
  /** Bedrag, datum en het verhaal-spoor liggen vast zodra de boete is doorgezet (eerst heropenen). */
  magWijzigen: boolean
  /** De bestuurder mag eraf (niet als er al is ingehouden of buiten de loonstrook om is verhaald). */
  magLoskoppelen: boolean
  /** Verwijderen kan alleen bij een handmatig ingevoerde boete die nog niet is doorgezet. */
  magVerwijderen: boolean
}

/** Resultaat van de bestuurder-resolver (toon "wie eruit komt" vóór opslaan). */
export interface BoeteResolveResultDTO {
  employeeId: string | null
  employeeNaam: string | null
  bestuurderBron: BoeteBestuurderBron
  toewijzingId: string | null
  /** batch-66 (FO-497) — waarom er (g)een bestuurder uitkomt ("wissel op deze dag"). */
  hint?: string | null
  /** batch-66 (FO-497) — er waren toewijzingen, maar geen eenduidige. */
  ambigu?: boolean
}

// ════════════════════════════════════════════════════════════════════════════
// batch-66 inkoop-afwijkingen (FO-497) — de boeteflow: boetes uit
// inkoopfacturen, een boetes-overzicht, doorzetten naar de salarisadministratie,
// rijgedrag-signalen en de eigen boetes in de portal.
//
// `Boete.status` (BoeteStatus) is de waarheid. Het oude `verrekenStatus` is voor
// de applicatie alleen-lezen en volgt de status via een DB-trigger (migratie
// b66_boetes); de afbeelding staat als pure functie in @streep/utils.
// ════════════════════════════════════════════════════════════════════════════

// ─── Status ──────────────────────────────────────────────────────────────────

export const BOETE_STATUSSEN = [
  'NIEUW',
  'BESTUURDER_ONBEKEND',
  'TE_BEOORDELEN',
  'TE_VERREKENEN',
  'DOORGEZET_SALARIS',
  'DEELS_VERREKEND',
  'VERREKEND',
  'NIET_VERHAALD',
  'AFGEBOEKT',
] as const
export type BoeteStatus = (typeof BOETE_STATUSSEN)[number]

export const BOETE_STATUS_LABELS: Record<BoeteStatus, string> = {
  NIEUW: 'Nieuw',
  BESTUURDER_ONBEKEND: 'Bestuurder onbekend',
  TE_BEOORDELEN: 'Te beoordelen',
  TE_VERREKENEN: 'Te verrekenen',
  DOORGEZET_SALARIS: 'Doorgezet naar salaris',
  DEELS_VERREKEND: 'Deels verrekend',
  VERREKEND: 'Verrekend',
  NIET_VERHAALD: 'Niet verhaald',
  AFGEBOEKT: 'Afgeboekt',
}

export const BOETE_STATUS_BADGE_VARIANT: Record<
  BoeteStatus,
  'info' | 'warning' | 'success' | 'danger' | 'secondary'
> = {
  NIEUW: 'info',
  BESTUURDER_ONBEKEND: 'danger',
  TE_BEOORDELEN: 'warning',
  TE_VERREKENEN: 'warning',
  DOORGEZET_SALARIS: 'info',
  DEELS_VERREKEND: 'info',
  VERREKEND: 'success',
  NIET_VERHAALD: 'secondary',
  AFGEBOEKT: 'secondary',
}

/** Nog niet besloten: bestuurder bevestigen en bepalen of de boete verhaald wordt. */
export const BOETE_TE_BEOORDELEN_STATUSSEN = ['NIEUW', 'BESTUURDER_ONBEKEND', 'TE_BEOORDELEN'] as const

/**
 * Er staat nog geld open dat op de medewerker verhaald wordt. Dit is de set die
 * de loonstrook-bewaking volgt en die bij uitdienst in de eindafrekening hoort.
 */
export const BOETE_OPENSTAAND_STATUSSEN = ['TE_VERREKENEN', 'DOORGEZET_SALARIS', 'DEELS_VERREKEND'] as const

/**
 * De verreken-familie: beoordeeld, en wordt (of is) op de medewerker verhaald.
 * Alleen binnen deze familie beweegt een verrekening op de loonstrook de status;
 * een boete die nog beoordeeld wordt of bewust is afgesloten houdt haar status.
 */
export const BOETE_VERREKEN_FAMILIE_STATUSSEN = [
  'TE_VERREKENEN',
  'DOORGEZET_SALARIS',
  'DEELS_VERREKEND',
  'VERREKEND',
] as const

/**
 * De opgave ligt bij de salarisadministratie (of is al ingehouden): bedrag,
 * datum, bestuurder en het verhaal-spoor liggen vast. Wijzigen kan pas na
 * heropenen, zodat de opgave aantoonbaar wordt ingetrokken.
 */
export const BOETE_NA_DOORZETTEN_STATUSSEN = ['DOORGEZET_SALARIS', 'DEELS_VERREKEND', 'VERREKEND'] as const

/**
 * De boete telt mee voor het rijgedrag van de medewerker: de bestuurder is
 * bevestigd. Wie de boete betaalt (medewerker of bedrijf) doet daarvoor niet ter
 * zake; een nog niet beoordeelde boete telt nooit mee. Een afgeboekte boete
 * telt alleen mee als zij eerder is beoordeeld (`beoordeeldOp` gevuld): een
 * boete die onbeoordeeld is afgeboekt (vervallen factuur) heeft alleen een
 * VOORGESTELDE bestuurder. De aanroeper legt die voorwaarde op.
 */
export const BOETE_RIJGEDRAG_STATUSSEN = [
  'TE_VERREKENEN',
  'DOORGEZET_SALARIS',
  'DEELS_VERREKEND',
  'VERREKEND',
  'NIET_VERHAALD',
  'AFGEBOEKT',
] as const

/**
 * Het statusfilter van het boetes-overzicht (querystring `status`, zoals de
 * declaratielijst; geen tabs-component).
 */
export const BOETE_STATUS_GROEPEN = [
  'TE_BEOORDELEN',
  'TE_VERREKENEN',
  'DOORGEZET',
  'VERREKEND',
  'AFGEBOEKT',
  'ALLE',
] as const
export type BoeteStatusGroep = (typeof BOETE_STATUS_GROEPEN)[number]

export const BOETE_STATUS_GROEP_LABELS: Record<BoeteStatusGroep, string> = {
  TE_BEOORDELEN: 'Te beoordelen',
  TE_VERREKENEN: 'Te verrekenen',
  DOORGEZET: 'Doorgezet naar salaris',
  VERREKEND: 'Verrekend',
  AFGEBOEKT: 'Afgeboekt',
  ALLE: 'Alle',
}

export const BOETE_STATUS_GROEP_STATUSSEN: Record<Exclude<BoeteStatusGroep, 'ALLE'>, readonly BoeteStatus[]> = {
  TE_BEOORDELEN: BOETE_TE_BEOORDELEN_STATUSSEN,
  TE_VERREKENEN: ['TE_VERREKENEN'],
  DOORGEZET: ['DOORGEZET_SALARIS', 'DEELS_VERREKEND'],
  VERREKEND: ['VERREKEND'],
  AFGEBOEKT: ['NIET_VERHAALD', 'AFGEBOEKT'],
}

// ─── Bron, verhaalbeslissing, rijgedrag ──────────────────────────────────────

export const BOETE_BRONNEN = ['HANDMATIG', 'INKOOPFACTUUR', 'DOCUMENT'] as const
export type BoeteBron = (typeof BOETE_BRONNEN)[number]

export const BOETE_BRON_LABELS: Record<BoeteBron, string> = {
  HANDMATIG: 'Handmatig ingevoerd',
  INKOOPFACTUUR: 'Uit een inkoopfactuur',
  DOCUMENT: 'Uit een geüpload document',
}

export const BOETE_VERHAAL_BESLISSINGEN = ['VERHALEN', 'BEDRIJF_DRAAGT'] as const
export type BoeteVerhaalBeslissing = (typeof BOETE_VERHAAL_BESLISSINGEN)[number]

export const BOETE_VERHAAL_BESLISSING_LABELS: Record<BoeteVerhaalBeslissing, string> = {
  VERHALEN: 'Verhalen op de medewerker',
  BEDRIJF_DRAAGT: 'Het bedrijf draagt de boete',
}

export const RIJGEDRAG_SIGNAAL_REDENEN = ['KORTE_TERMIJN', 'LANGE_TERMIJN', 'ERNSTIG'] as const
export type RijgedragSignaalReden = (typeof RIJGEDRAG_SIGNAAL_REDENEN)[number]

export const RIJGEDRAG_SIGNAAL_REDEN_LABELS: Record<RijgedragSignaalReden, string> = {
  KORTE_TERMIJN: 'Meerdere boetes in korte tijd',
  LANGE_TERMIJN: 'Meerdere boetes in een jaar',
  ERNSTIG: 'Ernstige overtreding',
}

/** Het gesprekstype dat bij een rijgedrag-signaal wordt voorgesteld (seed + migratie). */
export const GESPREK_TYPE_KEY_VERKEERSGEDRAG = 'VERKEERSGEDRAG'

/** De kostensoortcode (stamtabel, batch-65) waarop een factuurregel als boete telt. */
export const BOETE_KOSTENSOORT_CODE = 'BEL_BOETES'

// ─── Audit ───────────────────────────────────────────────────────────────────

/**
 * `BoeteAudit.actie` is in de DB een vrije string. Deze lijst houdt hem
 * consistent: de service schrijft alleen deze waarden. De eerste acht bestonden
 * al (FO-033 en FO-433).
 */
export const BOETE_AUDIT_ACTIES = [
  'AANGEMAAKT',
  'BIJGEWERKT',
  'BESTUURDER_GEWIJZIGD',
  'VERHAALD',
  'VERWIJDERD',
  'VERREKEND',
  'VERREKENING_ONTKOPPELD',
  'AFGEBOEKT',
  'UIT_FACTUUR',
  'UIT_DOCUMENT',
  'ZELFDE_KENMERK',
  'VERHOOGD',
  'BEOORDEELD',
  'STATUS_GEWIJZIGD',
  'DOORGEZET',
  'MEDEWERKER_GEMELD',
  'HERINNERING_SALARIS',
  'BEZWAAR',
  'REGEL_HERANKERD',
  'OPGAVE_INGETROKKEN',
  'OPNIEUW_GEFACTUREERD',
  'INGEHOUDEN_BIJ_VORIGE_BESTUURDER',
  'ADMINISTRATIEKOSTEN',
  'TEN_ONRECHTE_INGEHOUDEN',
  'VERHAAL_TERUGGEDRAAID',
  'FACTUUR_INTERN',
] as const
export type BoeteAuditActie = (typeof BOETE_AUDIT_ACTIES)[number]

export const BOETE_AUDIT_ACTIE_LABELS: Record<BoeteAuditActie, string> = {
  AANGEMAAKT: 'Geregistreerd',
  BIJGEWERKT: 'Bijgewerkt',
  BESTUURDER_GEWIJZIGD: 'Bestuurder gewijzigd',
  VERHAALD: 'Verhaald',
  VERWIJDERD: 'Verwijderd',
  VERREKEND: 'Verrekend op de loonstrook',
  VERREKENING_ONTKOPPELD: 'Verrekening ontkoppeld',
  AFGEBOEKT: 'Afgeboekt',
  UIT_FACTUUR: 'Ontstaan uit een inkoopfactuur',
  UIT_DOCUMENT: 'Ontstaan uit een geüpload document',
  ZELFDE_KENMERK: 'Zelfde kenmerk opnieuw ontvangen',
  VERHOOGD: 'Bedrag verhoogd',
  BEOORDEELD: 'Beoordeeld',
  STATUS_GEWIJZIGD: 'Status gewijzigd',
  DOORGEZET: 'Doorgezet naar salaris',
  MEDEWERKER_GEMELD: 'Medewerker geïnformeerd',
  HERINNERING_SALARIS: 'Herinnering naar de salarisadministratie',
  BEZWAAR: 'Bezwaar van de medewerker',
  REGEL_HERANKERD: 'Factuurregel gewijzigd',
  OPGAVE_INGETROKKEN: 'Opgave aan de salarisadministratie ingetrokken',
  OPNIEUW_GEFACTUREERD: 'Opnieuw gefactureerd',
  INGEHOUDEN_BIJ_VORIGE_BESTUURDER: 'Ingehouden bij een vorige bestuurder',
  ADMINISTRATIEKOSTEN: 'Administratiekosten van de factuur',
  TEN_ONRECHTE_INGEHOUDEN: 'Ten onrechte ingehouden op de loonstrook',
  VERHAAL_TERUGGEDRAAID: 'Verhaal buiten de loonstrook teruggedraaid',
  FACTUUR_INTERN: 'De inkoopfactuur blijkt een interne factuur',
}

// ─── Foutcodes ───────────────────────────────────────────────────────────────

export const BOETE_FOUTCODES = {
  KENMERK_BESTAAT: 'BOETE_KENMERK_BESTAAT',
  OVERGANG: 'BOETE_OVERGANG_NIET_TOEGESTAAN',
  GEEN_BESTUURDER: 'BOETE_GEEN_BESTUURDER',
  BEZWAAR_OPEN: 'BOETE_BEZWAAR_OPEN',
  GEEN_SALARISADRES: 'GEEN_SALARISVERWERKER_ADRES',
  NIETS_DOOR_TE_ZETTEN: 'NIETS_DOOR_TE_ZETTEN',
  MEDEWERKER_NIET_BEREIKBAAR: 'MEDEWERKER_NIET_BEREIKBAAR',
  AFWIJKING_OPEN: 'BOETE_AFWIJKING_OPEN',
  /** Bedrag, datum, verhaal-spoor of verwijderen na het doorzetten: eerst heropenen. */
  NA_DOORZETTEN_VAST: 'BOETE_NA_DOORZETTEN_VAST',
  /** Een boete uit een inkoopfactuur wordt afgeboekt, niet verwijderd. */
  UIT_FACTUUR: 'BOETE_UIT_FACTUUR',
  /** De opgave ligt bij de salarisadministratie: bevestig dat zij is ingelicht. */
  SALARIS_NIET_GEINFORMEERD: 'BOETE_SALARIS_NIET_GEINFORMEERD',
  /** Er is al (een deel) ingehouden bij de huidige bestuurder. */
  AL_INGEHOUDEN: 'BOETE_AL_INGEHOUDEN',
  /** De datum van de overtreding is onbekend: eerst invullen of bevestigen. */
  DATUM_ONBEKEND: 'BOETE_DATUM_ONBEKEND',
} as const

// ─── Zod: lijst ──────────────────────────────────────────────────────────────

export const boeteOverzichtQuerySchema = z.object({
  status: z.enum(BOETE_STATUS_GROEPEN).default('TE_BEOORDELEN'),
  employeeId: idParam('employeeId').optional(),
  voertuigId: idParam('voertuigId').optional(),
  entityId: idParam('entityId').optional(),
  soort: z.enum(BOETE_SOORTEN).optional(),
  bron: z.enum(BOETE_BRONNEN).optional(),
  van: isoDate.optional(),
  tot: isoDate.optional(),
  bedragMin: z.coerce.number().min(0).optional(),
  bedragMax: z.coerce.number().min(0).optional(),
  zoek: z.string().trim().max(100).optional(),
  page: z.coerce.number().int().positive().default(1),
  pageSize: z.coerce.number().int().positive().max(100).default(25),
})
export type BoeteOverzichtQuery = z.infer<typeof boeteOverzichtQuerySchema>

// ─── Zod: beoordelen, status, bezwaar ────────────────────────────────────────

/**
 * De beoordeling: de bestuurder bevestigen en beslissen of de boete verhaald
 * wordt. "Het bedrijf draagt" vraagt een reden; "verhalen" vraagt een
 * bestuurder (zonder bestuurder valt er niets te verhalen).
 */
export const boeteBeoordelenSchema = z
  .object({
    employeeId: idParam('employeeId').nullable(),
    verhaalBeslissing: z.enum(BOETE_VERHAAL_BESLISSINGEN),
    reden: z.string().trim().max(2000, 'De reden is te lang').nullable().optional(),
  })
  .superRefine((d, ctx) => {
    if (d.verhaalBeslissing === 'VERHALEN' && !d.employeeId) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Kies de bestuurder op wie de boete verhaald wordt.',
        path: ['employeeId'],
      })
    }
    if (d.verhaalBeslissing === 'BEDRIJF_DRAAGT' && (d.reden ?? '').trim().length < 5) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Geef aan waarom het bedrijf deze boete draagt (minstens 5 tekens).',
        path: ['reden'],
      })
    }
  })
export type BoeteBeoordelenInvoer = z.infer<typeof boeteBeoordelenSchema>

/** Heropenen (terug naar Te beoordelen) of afboeken; altijd met reden. */
export const boeteStatusSchema = z.object({
  naar: z.enum(['TE_BEOORDELEN', 'AFGEBOEKT']),
  reden: z.string().trim().min(5, 'Geef een reden op (minstens 5 tekens).').max(2000, 'De reden is te lang'),
  /**
   * Ligt de opgave al bij de salarisadministratie, dan vervalt die met deze
   * handeling. De gebruiker bevestigt dat de salarisadministratie is ingelicht;
   * zonder die bevestiging weigert de API.
   */
  salarisGeinformeerd: z.boolean().optional(),
})
export type BoeteStatusInvoer = z.infer<typeof boeteStatusSchema>

/**
 * "Toch niet buiten de loonstrook om verhaald": het bedrag dat met "Markeer
 * verhaald" is vastgelegd vervalt. Altijd met reden (het was een vergissing, of
 * het bedrag is terugbetaald).
 */
export const boeteVerhaalTerugdraaienSchema = z.object({
  reden: z.string().trim().min(5, 'Geef een reden op (minstens 5 tekens).').max(2000, 'De reden is te lang'),
})
export type BoeteVerhaalTerugdraaienInvoer = z.infer<typeof boeteVerhaalTerugdraaienSchema>

/** Bezwaar van de medewerker in de portal ("ik reed niet"). */
export const boeteBezwaarSchema = z.object({
  toelichting: z
    .string()
    .trim()
    .min(5, 'Leg kort uit waarom je bezwaar maakt (minstens 5 tekens).')
    .max(2000, 'De toelichting is te lang'),
})
export type BoeteBezwaarInvoer = z.infer<typeof boeteBezwaarSchema>

// ─── Zod: doorzetten naar salaris ────────────────────────────────────────────

export const boeteDoorzettenSchema = z.object({
  periode: z.string().regex(BOETE_PERIODE_RE, 'Kies de verrekenperiode (maand en jaar).'),
  boeteIds: z
    .array(idParam('boeteId'))
    .min(1, 'Kies minstens één boete om door te zetten.')
    .max(200, 'Zet hoogstens 200 boetes tegelijk door.'),
  /**
   * De documenten van de boetes meesturen. Een inkoopfactuur gaat alleen mee als
   * zij uitsluitend boetes uit deze opgave bevat (een verzamelfactuur noemt ook
   * andere medewerkers en andere kosten); het voorbeeld toont wat er meegaat.
   */
  factuurPdfsBijvoegen: z.boolean().default(true),
  /**
   * Medewerkers zonder portal-account krijgen geen melding vooraf. Doorzetten
   * kan dan alleen als de gebruiker per medewerker bevestigt dat die op een
   * andere manier is geïnformeerd.
   */
  handmatigGeinformeerd: z.array(idParam('employeeId')).max(200).default([]),
})
export type BoeteDoorzettenInvoer = z.infer<typeof boeteDoorzettenSchema>

// ─── Zod: instellingen ───────────────────────────────────────────────────────

export const boeteInstellingenSchema = z.object({
  /** Soorten waarbij "het bedrijf draagt" de voorgestelde beslissing is. */
  bedrijfDraagtSoorten: z.array(z.enum(BOETE_SOORTEN)).max(BOETE_SOORTEN.length),
  /** Hoogste inhouding per medewerker per maand; de rest schuift door in termijnen. */
  termijnMaxPerMaand: z
    .number({ message: 'Vul een geldig bedrag in' })
    .min(1, 'Het termijnbedrag moet minstens 1 euro zijn')
    .max(100_000, 'Het termijnbedrag is onrealistisch hoog'),
  drempelKortAantal: z.number().int().min(1, 'Minstens 1').max(50),
  drempelKortDagen: z.number().int().min(1, 'Minstens 1 dag').max(365),
  drempelLangAantal: z.number().int().min(1, 'Minstens 1').max(50),
  drempelLangMaanden: z.number().int().min(1, 'Minstens 1 maand').max(60),
  ernstigeSoorten: z.array(z.enum(BOETE_SOORTEN)).max(BOETE_SOORTEN.length),
  /** Vanaf hoeveel km/u te hard een snelheidsboete als ernstig telt. */
  ernstigeSnelheidKmu: z.number().int().min(1, 'Minstens 1 km/u').max(200),
  /** Herinnering naar de salarisadministratie automatisch na de periode (default uit). */
  herinneringAutomatisch: z.boolean(),
})
export type BoeteInstellingenInvoer = z.infer<typeof boeteInstellingenSchema>

export interface BoeteInstellingenDTO extends BoeteInstellingenInvoer {
  bijgewerktOp: string | null
}

export const BOETE_INSTELLINGEN_STANDAARD: BoeteInstellingenInvoer = {
  bedrijfDraagtSoorten: [],
  termijnMaxPerMaand: 150,
  drempelKortAantal: 2,
  drempelKortDagen: 90,
  drempelLangAantal: 3,
  drempelLangMaanden: 12,
  ernstigeSoorten: ['ROOD_LICHT', 'TELEFOON_GEBRUIK'],
  ernstigeSnelheidKmu: 30,
  herinneringAutomatisch: false,
}

// ─── DTO's ───────────────────────────────────────────────────────────────────

/** Eén termijn van het verrekenplan. */
export interface BoeteTermijnDTO {
  periode: string
  bedrag: number
}

/**
 * Waar de open afwijking staat die het doorzetten van een boete tegenhoudt: op
 * haar eigen factuurregel of factuur, of op de regel of factuur van een
 * verhoging die in het boetebedrag zit (een aanmaning op een latere factuur).
 */
export const BOETE_AFWIJKING_OP = ['REGEL', 'FACTUUR', 'VERHOGING_REGEL', 'VERHOGING_FACTUUR'] as const
export type BoeteAfwijkingOp = (typeof BOETE_AFWIJKING_OP)[number]

/** De zin in het detail van de boete, per plek. */
export const BOETE_AFWIJKING_OP_TEKST: Record<BoeteAfwijkingOp, string> = {
  REGEL: 'Op de factuurregel van deze boete staat een afwijking open.',
  FACTUUR: 'Op de inkoopfactuur van deze boete staat een afwijking open.',
  VERHOGING_REGEL: 'Op de factuurregel van een verhoging van deze boete staat een afwijking open.',
  VERHOGING_FACTUUR: 'Op de inkoopfactuur van een verhoging van deze boete staat een afwijking open.',
}

export interface BoeteOverzichtRijDTO {
  id: string
  datum: string
  /** De overtredingsdatum staat niet op de factuur; `datum` is de factuurdatum. */
  datumOnzeker: boolean
  overtredingTijd: string | null
  soort: BoeteSoort
  soortLabel: string
  status: BoeteStatus
  statusLabel: string
  bron: BoeteBron
  bedrag: number
  administratieKosten: number | null
  /** Bedrag min wat al verrekend is. */
  openBedrag: number
  kenmerk: string | null
  kenteken: string | null
  voertuigId: string | null
  employeeId: string | null
  employeeNaam: string | null
  bestuurderBron: BoeteBestuurderBron
  bestuurderBronLabel: string
  entityNaam: string | null
  inkoopFactuurId: string | null
  inkoopFactuurNr: string | null
  leverancierNaam: string | null
  bezwaarOpen: boolean
  doorgezetPeriode: string | null
  /** Er staat een open afwijking in het afwijkingen-register die het doorzetten tegenhoudt. */
  afwijkingOpen: boolean
  /** Waar die afwijking op staat (null = geen). */
  afwijkingOp: BoeteAfwijkingOp | null
  /** De inkoopfactuur waar die afwijking op staat; bij een verhoging is dat de factuur van de verhoging. */
  afwijkingFactuurId: string | null
}

export interface BoeteOverzichtMetaDTO {
  total: number
  page: number
  pageSize: number
  /** Som over de HELE gefilterde set (nooit over de geladen pagina). */
  totaalBedrag: number
  totaalOpenBedrag: number
  /** Aantal per statusgroep binnen de overige filters (voor het statusfilter). */
  aantalPerGroep: Record<Exclude<BoeteStatusGroep, 'ALLE'>, number>
}

export interface BoeteAuditDTO {
  id: string
  actie: string
  actorNaam: string | null
  toelichting: string | null
  createdAt: string
}

export interface BoeteVerhogingDTO {
  id: string
  bedrag: number
  reden: string
  inkoopFactuurId: string | null
  inkoopFactuurNr: string | null
  createdAt: string
}

/** Wie had het voertuig rond de overtredingsdag, en waar stonden zij ingepland. */
export interface BoetePlanningHintDTO {
  employeeId: string
  employeeNaam: string
  toewijzingVan: string
  toewijzingTot: string | null
  planning: { titel: string; locatie: string | null; van: string; tot: string }[]
}

/** Een ingetrokken opgave die nog bewaakt wordt (detail van een boete). */
export interface BoeteIngetrokkenOpgaveDTO {
  periode: string
  periodeLabel: string
  bedrag: number
  op: string
  /** Op wiens naam de opgave stond. */
  employeeNaam: string | null
  /** De opgave stond op een ANDERE medewerker dan de huidige bestuurder. */
  andereMedewerker: boolean
}

/** Een eerdere opgave die door een nieuwe opgave wordt vervangen (voorbeeld, mail en bijlage). */
export interface BoeteVervangtDTO {
  periodeLabel: string
  bedrag: number
  /** Gevuld als de eerdere opgave op een andere medewerker stond. */
  opNaamVan: string | null
}

export interface BoeteDetailDTO extends BoeteOverzichtRijDTO {
  omschrijving: string | null
  locatie: string | null
  feitcode: string | null
  overschrijdingKmu: number | null
  kentekenVermeld: string | null
  inkoopRegelPosition: number | null
  inkoopRegelOmschrijving: string | null
  /** De factuur heeft een PDF die via de bestaande factuurpagina te openen is. */
  heeftFactuurPdf: boolean
  employeeDocumentId: string | null
  verhaalBeslissing: BoeteVerhaalBeslissing | null
  verhaalReden: string | null
  /** De voorgestelde beslissing volgens de instellingen (per soort). */
  voorgesteldeBeslissing: BoeteVerhaalBeslissing
  beoordeeldOp: string | null
  beoordeeldDoorNaam: string | null
  bezwaarOp: string | null
  bezwaarToelichting: string | null
  doorgezetOp: string | null
  doorgezetDoorNaam: string | null
  medewerkerGemeldOp: string | null
  verrekendBedrag: number
  verrekenplan: BoeteTermijnDTO[]
  afgeboektReden: string | null
  verhogingen: BoeteVerhogingDTO[]
  audits: BoeteAuditDTO[]
  planningHint: BoetePlanningHintDTO[]
  /**
   * De opgaven aan de salarisadministratie die zijn ingetrokken (heropend,
   * bezwaar, verhoging, afgeboekt, teruggedraaid) en nog bewaakt worden. Een
   * opgave vervalt uit de lijst nadat de loonrun na haar laatste termijn is
   * gecontroleerd.
   */
  ingetrokkenOpgaven: BoeteIngetrokkenOpgaveDTO[]
  /** Wat buiten de loonstrook om op de medewerker is verhaald (telt mee in wat openstaat). */
  verhaaldBuitenLoonstrook: number
  /** De opgave ligt nu bij de salarisadministratie (heropenen of afboeken trekt haar in). */
  opgaveBijSalaris: boolean
  /** De toegestane volgende statussen (de UI toont alleen wat kan). */
  magBeoordelen: boolean
  magAfboeken: boolean
  magHeropenen: boolean
  /** Bedrag, datum en kenmerk zijn nog te wijzigen (niet meer na het doorzetten). */
  magGegevensWijzigen: boolean
  /** Het verhaal buiten de loonstrook om is terug te draaien (niet zolang de boete bij de salarisadministratie ligt). */
  magVerhaalTerugdraaien: boolean
}

// ─── Doorzetten: voorbeeld + resultaat ───────────────────────────────────────

export interface BoeteDoorzetMedewerkerDTO {
  employeeId: string
  naam: string
  personeelsnummer: string | null
  /** Heeft een actief portal-account: krijgt de melding vooraf. */
  bereikbaar: boolean
  boetes: {
    id: string
    datum: string
    kenmerk: string | null
    kenteken: string | null
    soortLabel: string
    openBedrag: number
    /**
     * Wat er van deze boete al op de loonstrook is ingehouden EN meegeteld (euro,
     * 0 = niets), op welke opgave dan ook. De opgave gaat alleen over de rest. Bij
     * een vervangende opgave staat het erbij: "vervangt" mag niet gelezen worden
     * als "betaal terug wat er al is ingehouden".
     */
    alVerrekend: number
    termijnen: BoeteTermijnDTO[]
    /** De eerdere, ingetrokken opgaven voor dezelfde boete die deze opgave vervangt. */
    vervangt: BoeteVervangtDTO[]
  }[]
  totaal: number
  /** Wat er in de gekozen periode wordt ingehouden (na de termijngrens). */
  inDezePeriode: number
  inTermijnen: boolean
}

/** Een document van een boete in de opgave, en of het met de mail meegaat. */
export interface BoeteDoorzetBijlageDTO {
  naam: string
  meegestuurd: boolean
  /** Waarom het document niet meegaat (null als het meegaat). */
  reden: string | null
}

export interface BoeteDoorzetVoorbeeldDTO {
  periode: string
  periodeLabel: string
  /** De ingestelde adressen; null = niet ingesteld (doorzetten weigert). */
  ontvangers: { naam: string | null; to: string[]; cc: string[] } | null
  termijnMaxPerMaand: number
  medewerkers: BoeteDoorzetMedewerkerDTO[]
  /** Boetes die niet doorgezet kunnen worden, met de reden. */
  overgeslagen: { id: string; kenmerk: string | null; reden: string }[]
  /**
   * De documenten van de boetes (factuur-PDF, geüploade bekeuring) en of zij
   * meegaan als de gebruiker ervoor kiest. Een factuur met ook andere regels
   * (andere medewerkers, andere kosten) gaat nooit mee.
   */
  bijlagen: BoeteDoorzetBijlageDTO[]
  aantal: number
  totaal: number
}

export interface BoeteDoorzetResultaatDTO {
  doorzettingId: string
  aantal: number
  totaal: number
  /** De mail is niet bezorgd: de boetes zijn NIET doorgezet. */
  nietBezorgd: boolean
  waarschuwing: string | null
  /** Documenten die niet zijn meegestuurd (te groot, niet gevonden, of een factuur met ook andere regels). */
  bijlagenOvergeslagen: string[]
}

export interface BoeteDoorzettingDTO {
  id: string
  periode: string
  periodeLabel: string
  aantal: number
  totaal: number
  doorNaam: string | null
  createdAt: string
  herinneringOp: string | null
  /** Aantal boetes uit deze doorzetting dat nog niet (volledig) verrekend is. */
  nogOpen: number
  /**
   * Aantal boetes dat ACHTERLOOPT op het verrekenplan: er had volgens het plan
   * al meer ingehouden moeten zijn dan er op de loonstroken staat. Een boete in
   * termijnen die op schema ligt telt hier niet mee.
   */
  achterstand: number
  achterstandBedrag: number
  /** De stand van de mail aan de salarisadministratie. */
  mailStand: BoeteDoorzettingMailStand
}

export const BOETE_DOORZETTING_MAIL_STANDEN = ['IN_WACHTRIJ', 'VERSTUURD', 'MISLUKT'] as const
export type BoeteDoorzettingMailStand = (typeof BOETE_DOORZETTING_MAIL_STANDEN)[number]

// ─── Rijgedrag ───────────────────────────────────────────────────────────────

export interface RijgedragSamenvattingDTO {
  /** Laatste 12 maanden, alleen bevestigde boetes. */
  aantal: number
  bedrag: number
  perSoort: { soort: BoeteSoort; soortLabel: string; aantal: number; bedrag: number }[]
  /** Schademeldingen in dezelfde periode (FO-462), ter vergelijking. */
  schades: number
  /** Het laatste openstaande signaal, of null. */
  signaal: {
    id: string
    redenen: RijgedragSignaalReden[]
    createdAt: string
    afgehandeldOp: string | null
  } | null
}

// ─── Portal: eigen boetes ────────────────────────────────────────────────────

export interface MijnBoeteDTO {
  id: string
  datum: string
  overtredingTijd: string | null
  soortLabel: string
  locatie: string | null
  kenteken: string | null
  bedrag: number
  openBedrag: number
  status: BoeteStatus
  /** Leesbare status voor de medewerker (geen interne termen). */
  statusTekst: string
  verrekenplan: BoeteTermijnDTO[]
  bezwaarOp: string | null
  /** Bezwaar maken kan (nog niet verrekend en nog geen bezwaar in behandeling). */
  magBezwaar: boolean
}
